import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import firebase from '../firebase/firebaseIndex';
import { authMethods } from '../firebase/authmethods';
import { authError, clearSessionCache, writeStored } from '../firebase/authSession';
import checkLessonAccess from '../stripe/checkLessonAccess';

export const firebaseAuth = React.createContext();
const initialSession = { status: 'initializing', user: null, token: null, accessStatus: 'idle', sessionError: null };

export function sessionReducer(state, action) {
  switch (action.type) {
    case 'INITIALIZING': return { ...initialSession };
    case 'SIGNED_OUT': return { ...initialSession, status: 'signedOut' };
    case 'AUTHENTICATED': return { status: 'authenticated', user: action.user, token: action.token, accessStatus: 'loading', sessionError: null };
    case 'ACCESS': return state.user && state.user.uid === action.uid ? { ...state, accessStatus: action.status } : state;
    case 'ERROR': return { ...initialSession, status: 'error', sessionError: authError(action.error) };
    default: return state;
  }
}

export function withTimeout(operation, milliseconds = 15000) {
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => reject({ code: 'auth/network-request-failed' }), milliseconds);
  });
  return Promise.race([operation, deadline]).finally(() => clearTimeout(timer));
}

export default function AuthProvider({ children }) {
  const [session, dispatch] = useReducer(sessionReducer, initialSession);
  const [inputs, setInputs] = useState({ email: '', password: '', license: '' });
  const [errors, setErrors] = useState([]);
  const revision = useRef(0);
  const accessRevision = useRef(0);

  const loadAccess = useCallback(async (user, sessionRequest) => {
    const accessRequest = ++accessRevision.current;
    dispatch({ type: 'ACCESS', uid: user.uid, status: 'loading' });
    try {
      const paid = await withTimeout(checkLessonAccess(user));
      if (sessionRequest !== revision.current || accessRequest !== accessRevision.current) return;
      writeStored('paymentFlag', String(paid === true));
      dispatch({ type: 'ACCESS', uid: user.uid, status: paid === true ? 'granted' : 'denied' });
    } catch (_) {
      if (sessionRequest === revision.current && accessRequest === accessRevision.current) {
        writeStored('paymentFlag', 'false');
        dispatch({ type: 'ACCESS', uid: user.uid, status: 'error' });
      }
    }
  }, []);

  const refreshSession = useCallback(async user => {
    const request = ++revision.current;
    ++accessRevision.current;
    clearSessionCache();
    dispatch({ type: 'INITIALIZING' });
    if (!user) { dispatch({ type: 'SIGNED_OUT' }); return; }
    try {
      const token = await withTimeout(user.getIdToken());
      if (request !== revision.current || !firebase.auth().currentUser || firebase.auth().currentUser.uid !== user.uid) return;
      writeStored('token', token);
      dispatch({ type: 'AUTHENTICATED', user, token });
      setInputs(previous => ({ ...previous, password: '' }));
      await loadAccess(user, request);
    } catch (error) {
      if (request === revision.current) dispatch({ type: 'ERROR', error });
    }
  }, [loadAccess]);

  useEffect(() => {
    const unsubscribe = firebase.auth().onIdTokenChanged(refreshSession, error => {
      ++revision.current;
      ++accessRevision.current;
      clearSessionCache();
      dispatch({ type: 'ERROR', error });
    });
    return () => { ++revision.current; ++accessRevision.current; unsubscribe(); };
  }, [refreshSession]);

  const refreshAccess = useCallback(() => {
    const user = firebase.auth().currentUser;
    if (user) return loadAccess(user, revision.current);
    return Promise.resolve();
  }, [loadAccess]);
  const retrySession = useCallback(() => refreshSession(firebase.auth().currentUser), [refreshSession]);
  const handleSignout = useCallback(async () => {
    await authMethods.signout(setErrors);
    await refreshSession(null);
  }, [refreshSession]);
  const handlesigninGoogle = useCallback(() => authMethods.signinGoogle(setErrors), []);
  const handleSignup = () => authMethods.signup(inputs.email, inputs.password, inputs.license, setErrors);
  const handleSignin = () => authMethods.signin(inputs.email, inputs.password, setErrors);
  const handleForgotPassword = () => authMethods.forgotPassword(inputs.email, setErrors);
  const handleSigninEmailLink = () => authMethods.sendEmailLink(inputs.email, setErrors);
  const handleCompleteEmailLink = useCallback((email, link) => authMethods.completeEmailLink(email, link, setErrors), []);

  return <firebaseAuth.Provider value={{
    sessionStatus: session.status, sessionError: session.sessionError, accessStatus: session.accessStatus,
    currentUser: session.user, currentUserId: session.user ? session.user.uid : null,
    token: session.token, paymentFlag: session.accessStatus === 'granted', loading: session.status === 'initializing',
    inputs, setInputs, errors, setErrors, refreshAccess, retrySession,
    handleSignup, handleSignupLicense: handleSignup, handleSignin, handlesigninGoogle,
    handleSignout, handleForgotPassword, handleSigninEmailLink, handleCompleteEmailLink,
    handleSigninGoogleAfterRedirect: refreshSession,
  }}>{children}</firebaseAuth.Provider>;
}
